import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Package, Shield, Eye, Users, ArrowRight } from "lucide-react";

const HeroSection = () => {
  const whatsappNumber = "33624570308";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Bonjour, j'ai une question concernant GoBledPro.")}`;

  return (
    <section className="relative min-h-[85vh] flex items-center pt-6 md:pt-16 pb-8 overflow-x-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
      
      {/* Decorative elements - hidden on mobile to prevent overflow */}
      <div className="hidden md:block absolute top-1/4 right-0 w-72 h-72 bg-secondary/10 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-1/4 left-0 w-56 h-56 bg-primary/10 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10 max-w-full overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-10 items-center">
          {/* Left Content */}
          <div className="lg:text-left max-w-full overflow-hidden">
            {/* Badge - Beta slightly larger for better readability */}
            <div className="inline-flex items-center bg-muted/60 border border-border/40 rounded-lg px-4 py-2 mb-3 md:mb-4">
              <span className="text-sm text-muted-foreground font-medium">
                🚀 Plateforme en beta test
              </span>
            </div>
            
            {/* Title - 3 lines premium, tighter spacing, responsive sizing */}
            <h1 className="text-[1.875rem] sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3 animate-fade-up delay-100 leading-[1.1]">
              <span className="block">Transport de colis</span>
              <span className="block text-[2.125rem] sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                <span className="inline-flex items-center gap-1 flex-wrap">
                  <span className="font-bold">
                    <span className="text-[#002395]">Fr</span><span className="text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">an</span><span className="text-[#ED2939]">ce</span>
                  </span>
                  <span className="text-foreground"> ⇄ </span>
                  <span className="font-bold">
                    <span className="text-[#C1272D]">Mar</span><span className="text-[#006233]">oc</span>
                  </span>
                </span>
              </span>
              <span className="block">en toute confiance</span>
            </h1>
            
            {/* Subtitle - same text on mobile and desktop */}
            <p className="text-base md:text-lg text-foreground/70 mb-2 md:mb-3 max-w-xl animate-fade-up delay-200 leading-relaxed">
              GoBledPro met en relation particuliers et transporteurs indépendants sélectionnés, pour des envois fiables France ⇄ Maroc.
            </p>
            
            {/* Micro-phrase reassurance */}
            <p className="text-[13px] md:text-sm text-muted-foreground mb-5 md:mb-6 animate-fade-up delay-200">
              Un cadre clair, avec des transporteurs identifiés.
            </p>
            
            {/* CTA - Dynamic premium button with micro-animation and tactile feedback */}
            <div className="flex flex-col gap-4 md:gap-3 lg:max-w-sm animate-fade-up delay-300 mt-2 md:mt-0">
              <Link to="/demande-transport" className="w-full">
                <Button 
                  size="lg" 
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-base md:text-base px-6 h-[56px] md:h-14 transition-all duration-150 hover:scale-[1.02] hover:shadow-lg active:scale-[0.96] active:shadow-sm active:bg-primary/80 shadow-md gap-2"
                >
                  Trouver un transport
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              
              {/* Trust proof - premium centered with micro-icons, better spacing */}
              <div className="flex flex-col items-center lg:items-start gap-2">
                <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-5 md:gap-4 text-[12px] sm:text-[13px] md:text-sm text-foreground/60 flex-wrap">
                  <span className="flex items-center gap-1.5 sm:gap-2">
                    <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary/70 flex-shrink-0" />
                    <span className="font-medium">Fiabilité</span>
                  </span>
                  <span className="text-border/60 hidden sm:inline">·</span>
                  <span className="flex items-center gap-1.5 sm:gap-2">
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary/70 flex-shrink-0" />
                    <span className="font-medium">Transparence</span>
                  </span>
                  <span className="text-border/60 hidden sm:inline">·</span>
                  <span className="flex items-center gap-1.5 sm:gap-2">
                    <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary/70 flex-shrink-0" />
                    <span className="font-medium">Encadrement</span>
                  </span>
                </div>
                {/* Secondary reassurance line */}
                <p className="text-[11px] md:text-xs text-muted-foreground/70 text-center lg:text-left">
                  Process encadré · Mise en relation structurée · Données protégées
                </p>
              </div>
              
            </div>
          </div>
          
          {/* Right Visual - Circular Package Illustration */}
          <div className="relative hidden lg:block animate-fade-up delay-300">
            <div className="relative w-[380px] h-[380px] mx-auto">
              {/* SVG for dynamic arc ring with animation */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 380 380">
                <defs>
                  <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(220, 70%, 45%)" />
                    <stop offset="100%" stopColor="hsl(220, 60%, 55%)" />
                  </linearGradient>
                </defs>
                {/* Background ring - lighter blue */}
                <circle
                  cx="190"
                  cy="190"
                  r="170"
                  fill="none"
                  stroke="hsl(220, 50%, 80%)"
                  strokeWidth="24"
                />
                {/* Dynamic arc - animated progression */}
                <circle
                  cx="190"
                  cy="190"
                  r="170"
                  fill="none"
                  stroke="url(#arcGradient)"
                  strokeWidth="24"
                  strokeLinecap="round"
                  strokeDasharray="800 1068"
                  strokeDashoffset="0"
                  transform="rotate(-90 190 190)"
                  className="animate-[arc-progress_8s_ease-in-out_infinite]"
                />
              </svg>
              
              {/* Inner light circle */}
              <div className="absolute inset-[24px] rounded-full bg-gradient-to-br from-[hsl(220,30%,94%)] via-[hsl(220,25%,96%)] to-[hsl(220,30%,92%)] shadow-inner">
                {/* Central package icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Package className="w-24 h-24 text-primary stroke-[1.5]" />
                </div>
              </div>
              
              {/* French cities around the circle - identical alignment, black color */}
              {/* Paris - top */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-foreground/40" />
                <span className="text-[10px] font-medium text-foreground/70 mt-0.5">Paris</span>
              </div>
              
              {/* Lyon - top right (homogène au cercle) */}
              <div className="absolute top-[18%] -right-4 flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-foreground/40" />
                <span className="text-[10px] font-medium text-foreground/70 mt-0.5">Lyon</span>
              </div>
              
              {/* Marseille - right middle */}
              <div className="absolute top-1/2 -right-4 -translate-y-1/2 flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-foreground/40" />
                <span className="text-[10px] font-medium text-foreground/70 mt-0.5">Marseille</span>
              </div>
              
              {/* Montpellier - bottom right */}
              <div className="absolute bottom-16 right-2 flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-foreground/40" />
                <span className="text-[10px] font-medium text-foreground/70 mt-0.5">Montpellier</span>
              </div>
              
              {/* Lille - top left (homogène au cercle) */}
              <div className="absolute top-[18%] -left-4 flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-foreground/40" />
                <span className="text-[10px] font-medium text-foreground/70 mt-0.5">Lille</span>
              </div>
              
              {/* Toulouse - left middle */}
              <div className="absolute top-1/2 -left-4 -translate-y-1/2 flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-foreground/40" />
                <span className="text-[10px] font-medium text-foreground/70 mt-0.5">Toulouse</span>
              </div>
              
              {/* Bordeaux - bottom left */}
              <div className="absolute bottom-16 left-2 flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-foreground/40" />
                <span className="text-[10px] font-medium text-foreground/70 mt-0.5">Bordeaux</span>
              </div>
              
              {/* FR badge - moved right */}
              <div className="absolute top-6 left-20 bg-white rounded-lg px-2 py-1.5 shadow-lg flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-[10px] font-bold text-primary-foreground">FR</span>
                </div>
                <div className="text-left">
                  <p className="text-xs font-semibold text-foreground">France</p>
                  <p className="text-[10px] text-muted-foreground">Départ</p>
                </div>
              </div>
              
              {/* Casablanca badge - bottom */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white rounded-lg px-2 py-1.5 shadow-lg flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-[hsl(145,70%,28%)] flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">MA</span>
                </div>
                <div className="text-left">
                  <p className="text-xs font-semibold text-foreground">Casablanca</p>
                  <p className="text-[10px] text-muted-foreground">Arrivée</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
